import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import { type DocumentDetails } from "~/api/types";
import { DocumentMetadataView } from "~/molecules/DocumentMetadataSections";
import { DocumentMetadataEdit } from "~/molecules/DocumentMetadataEdit";
import { useState } from "react";
import { canEditDocumentMetadata } from "~/helpers/documentPermissions";

interface Props {
  document: DocumentDetails;
  documentVersionId: number;
  onSave: () => void;
  onEditModeChange?: (isEditing: boolean) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

export function DocumentMetadata({
  document,
  documentVersionId,
  onSave,
  onEditModeChange,
  onUnsavedChangesChange,
}: Props) {
  const { t } = useTranslation();
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);

  const startEditing = () => {
    setIsEditingMetadata(true);
    onEditModeChange?.(true);
  };

  const stopEditing = () => {
    setIsEditingMetadata(false);
    onEditModeChange?.(false);
    onUnsavedChangesChange?.(false);
  };

  const selectedVersion = document.versions.find(
    (v) => v.id === documentVersionId,
  );
  const canEditMetadata = selectedVersion
    ? canEditDocumentMetadata(selectedVersion, document.archivedAt)
    : false;

  return (
    <div className="flex flex-col gap-4">
      {!isEditingMetadata ? (
        <>
          {canEditMetadata && (
            <Button variant="outline" onClick={startEditing}>
              {t(($) => $.commons.buttons.edit)}{" "}
              {t(($) => $.commons.document.metadata)}
            </Button>
          )}
          <DocumentMetadataView document={document} />
        </>
      ) : (
        canEditMetadata && (
          <DocumentMetadataEdit
            document={document}
            onSave={onSave}
            onCancel={stopEditing}
            onUnsavedChangesChange={onUnsavedChangesChange}
          />
        )
      )}
    </div>
  );
}
